<script setup>
import { computed, onMounted, ref, defineEmits } from "vue"

import Lottie from "@/components/widgets/lottie.vue"
import animationData from "@/components/widgets/msoeawqm.json"
import PersianDate from "persian-date"

import store from "../../state/store"
import useCategories from "../../use/useCategories"
import useDebouncedFunction from "../../use/useDebouncedFunction"
import TableHeaderCell from "@/components/table/TableHeaderCell.vue"

const categories = computed(() => store.state.categories)

const {
  categoryIsInMenu,
  search,
  perPage,
  sortField,
  sortDirection,
  getCategories,
  sortCategories,
} = useCategories()
const { handleInput } = useDebouncedFunction(getCategories)

const emit = defineEmits(["clickEdit"])

onMounted(() => {
  getCategories()
})

const toPersianDateTime = (date) => {
  const [datePart, timePart] = date.split(" ")
  const [year, month, day] = datePart.split("-")
  const [hour, minute, second] = timePart.split(":")
  const gregorianDate = new Date(year, month - 1, day, hour, minute, second)
  const persianDate = new PersianDate(gregorianDate)
  return persianDate.format("YYYY/MM/DD HH:mm:ss")
}

function getForPage(ev, link) {
  ev.preventDefault()
  if (!link.url || link.active) {
    return
  }
  getCategories(link.url)
}

const editCategory = (cat) => {
  emit("clickEdit", cat)
}

const defaultOptions = ref({
  animationData: animationData,
})
</script>

<template>
  <b-card-body class="border-bottom-dashed border-bottom">
    <b-row class="g-3">
      <b-col xl="7">
        <div class="search-box">
          <input
            v-model="search"
            @input="handleInput"
            type="text"
            class="form-control search"
            placeholder="جستجوی نام دسته بندی..."
          />
          <i class="ri-search-line search-icon"></i>
        </div>
      </b-col>

      <b-col xl="5">
        <b-row class="g-3">
          <b-col sm="6">
            <select
              @change="getCategories(null)"
              v-model="categoryIsInMenu"
              class="form-select mb-3"
              aria-label="Default select example"
            >
              <option value="" selected>انتخاب وضعیت دسته</option>
              <option value="0">عدم نمایش</option>
              <option value="1">نمایش</option>
            </select>
          </b-col>

          <b-col sm="6">
            <select
              @change="getCategories(null)"
              v-model="perPage"
              class="form-select mb-3"
              aria-label="Default select example"
            >
              <option value="" selected>نمایش تعداد ردیف</option>
              <option value="2">2</option>
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="30">30</option>
              <option value="50">50</option>
            </select>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
  </b-card-body>
  <b-card-body>
    <div>
      <div class="table-responsive table-card mb-1">
        <table class="table align-middle" id="categoryTable">
          <thead class="table-light text-muted">
            <tr>
              <th class="sort" data-sort="">تصویر</th>
              <TableHeaderCell
                field="category_name"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortCategories('category_name')"
              >
                نام دسته بندی
              </TableHeaderCell>
              <TableHeaderCell
                field="parent_id"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortCategories('parent_id')"
              >
                مادر دسته
              </TableHeaderCell>
              <TableHeaderCell
                field="category_slug"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortCategories('category_slug')"
              >
                لینک
              </TableHeaderCell>
              <TableHeaderCell
                field="is_in_menu"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortCategories('is_in_menu')"
              >
                وضعیت در منو
              </TableHeaderCell>
              <TableHeaderCell
                field="created_at"
                :sort-field="sortField"
                :sort-direction="sortDirection"
                @click="sortCategories('created_at')"
              >
                تاریخ ایجاد
              </TableHeaderCell>
              <th class="sort" data-sort="action">عملیات</th>
            </tr>
          </thead>

          <tbody
            class="list"
            v-if="categories.loading || !categories.data.length"
          >
            <tr>
              <td colspan="8">
                <div class="text-center">
                  <b-spinner
                    v-if="categories.loading"
                    variant="dark"
                    style="width: 3rem; height: 3rem"
                  ></b-spinner>
                  <div v-else>
                    <lottie
                      class="avatar-xl"
                      colors="primary:#121331,secondary:#08a88a"
                      :options="defaultOptions"
                      :height="75"
                      :width="75"
                    />
                    <h5 class="mt-2">هیچ دسته بندی وجود ندارد!</h5>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>

          <tbody class="list" v-else>
            <tr v-for="(category, index) of categories.data" :key="index">
              <td class="category_image">
                <div class="avatar-sm bg-light rounded p-1">
                  <img
                    v-if="category.image"
                    :src="category.image"
                    alt="category.name"
                    class="img-fluid d-block"
                  />
                </div>
              </td>
              <td class="category_name">{{ category.name }}</td>
              <td class="category_parent">
                {{
                  category.category_parent_name
                    ? category.category_parent_name
                    : "-"
                }}
              </td>
              <td class="category_slug">{{ category.slug }}</td>
              <td class="status">
                <span
                  class="badge text-uppercase"
                  :class="{
                    'badge-soft-success': category.is_in_menu == 1,
                    'badge-soft-danger': category.is_in_menu == 0,
                  }"
                  >{{ category.is_in_menu ? "نمایش" : "عدم نمایش" }}</span
                >
              </td>
              <td class="date">{{ toPersianDateTime(category.created_at) }}</td>
              <td>
                <ul class="list-inline hstack gap-2 mb-0">
                  <li
                    class="list-inline-item edit"
                    data-bs-toggle="tooltip"
                    data-bs-trigger="hover"
                    data-bs-placement="top"
                    title="Edit"
                  >
                    <b-link
                      variant="text-primary"
                      class="d-inline-block edit-item-btn"
                      @click="editCategory(category)"
                    >
                      <i class="ri-pencil-fill fs-16"></i>
                    </b-link>
                  </li>
                </ul>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        class="d-flex justify-content-end"
        v-if="categories.total > categories.limit"
      >
        <div class="pagination-wrap hstack gap-2" style="display: flex">
          <ul class="pagination listjs-pagination mb-0">
            <li
              v-for="(link, i) of categories.links"
              :key="i"
              class="page-item"
              :class="{
                disabled: !link.url,
                active: link.active || !link.url,
              }"
            >
              <span
                class="page-link"
                v-if="link.active"
                v-html="link.label"
              ></span>

              <a
                v-else
                href="#"
                @click="getForPage($event, link)"
                class="page-link"
                v-html="link.label"
              >
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </b-card-body>
</template>
